import {
  Button,
  Text,
  Group,
  CloseButton,
  LoadingOverlay,
  rem,
  Select,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { AddressCard } from './Cards/AddressCard';
import { PersonInfoCard } from './Cards/PersonInfoCard';
import { ContactPersonCard } from './Cards/ContactPersonCard';
import { StartCareDateCard } from './Cards/StartCareDateCard';
import { CareRequirementsCard } from './Cards/CareRequirementsCard';
import { dateFormat, dateFormatUI, locale } from '@/index';
import { createFormContext, zodResolver } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { getFetcher, postFetcher } from '@/api/fetchers';
import dayjs from 'dayjs';
import { type DateValue } from '@mantine/dates';
import { modals } from '@mantine/modals'; // Add this line
import { useEffect } from 'react';
import { isEmptyForm } from '@/utils/functions';
import { notifications } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';
import { type Services } from '@/schemas/API/GET/service/response';
import {
  type CreateApplicant,
  createApplicantSchema,
  defaultApplicant,
} from '@/schemas/API/POST/applicant/request';
import { type ApplicantDetailed } from '@/schemas/API/GET/applicant/response';
import { displayError } from '@/utils/error/errorHandler';

export const [
  ApplicantFormProvider,
  useApplicantFormContext,
  useApplicantForm,
] = createFormContext<CreateApplicant>();

export const NewApplicantEmptyFormPage: React.FC = () => {
  const navigate = useNavigate();

  const { data: availableServices } = useSWR<Services>('services', getFetcher);

  const form = useApplicantForm({
    validate: zodResolver(createApplicantSchema),
    validateInputOnChange: true,
  });

  useEffect(() => {
    form.setInitialValues(defaultApplicant);
    form.setValues(defaultApplicant);
  }, []);

  const handleCancel = (): void => {
    if (!form.isDirty()) {
      navigate('/applicants');
      return;
    }
    modals.openConfirmModal({
      title: 'Odejít bez uložení?',
      centered: true,
      children: (
        <Text>
          Formulář obsahuje změny, které nebyly uloženy. Určitě chcete formulář
          uzavřít?
        </Text>
      ),
      labels: { confirm: 'Odejít', cancel: 'Zrušit' },
      confirmProps: { color: 'blue' },
      onCancel: () => {},
      onConfirm: () => {
        navigate('/applicants');
      },
    });
  };

  const handleSave = async (): Promise<void> => {
    const validationResult = form.validate();

    if (validationResult.hasErrors) {
      console.error('Form failed validation:', validationResult.errors);
      notifications.show({
        title: `Zájemce nebylo možné uložit.`,
        message: `Formulář obsahuje chyby.`,
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
      return;
    }

    try {
      const applicant: ApplicantDetailed = await postFetcher(
        `/applicants`,
        form.values
      );
      navigate(`/applicants/${applicant.id}`);
    } catch (error) {
      displayError(error);
    }
  };

  if (!isEmptyForm(form)) {
    return (
      <ApplicantFormProvider form={form}>
        <div className="p-8">
          <div className="flex flex-col sm:rounded-card sm:border sm:border-card sm:p-2.5 gap-2.5">
            <Group justify="space-between">
              <Text fw={700} size="xl">
                Formulář zájemce
              </Text>
              <CloseButton onClick={() => handleCancel()} />
            </Group>

            <Group justify="space-between">
              <DateInput
                className="w-full max-w-[235px]"
                variant="filled"
                label="Datum komunikace"
                placeholder="Vyberte datum"
                valueFormat={dateFormatUI}
                locale={locale}
                value={
                  form.values.commDate === ''
                    ? dayjs().toDate()
                    : dayjs(form.values.commDate).toDate()
                }
                onChange={(d: DateValue) => {
                  const date: string = dayjs(d).format(dateFormat);
                  form.setFieldValue('commDate', date);
                }}
                error={form.getInputProps('commDate').error}
              />
              <Select
                label="Služba"
                onChange={e => {
                  form.setFieldValue('serviceId', parseInt(e ?? '0'));
                }}
                data={availableServices?.map(service => ({
                  value: service.id.toString(),
                  label: service.name,
                }))}
                placeholder="Vyberte službu"
                error={form.getInputProps('serviceId').error}
              />
            </Group>

            <PersonInfoCard
              title="Zájemce o službu"
              formContext={useApplicantFormContext}
            />

            <Group justify="space-between" grow className="flex items-start">
              <AddressCard
                addressType="Trvalá adresa"
                formContext={useApplicantFormContext}
              />
              <AddressCard
                addressType="Adresa poskytování"
                formContext={useApplicantFormContext}
              />
            </Group>

            <ContactPersonCard
              title="Kontaktující"
              formContext={useApplicantFormContext}
            />

            <StartCareDateCard formContext={useApplicantFormContext} />

            <CareRequirementsCard formContext={useApplicantFormContext} />

            <Group justify="flex-end">
              <Button variant="outline" onClick={handleCancel}>
                Zrušit
              </Button>

              <Button
                className="w-28"
                onClick={() => {
                  void handleSave();
                }}
              >
                Uložit
              </Button>
            </Group>
          </div>
        </div>
      </ApplicantFormProvider>
    );
  }

  return (
    <div className="relative" style={{ height: '100%', width: '100%' }}>
      <LoadingOverlay
        visible
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />
    </div>
  );
};
